import { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import Icon from "./Icon";

// Fase 4.1 + C1/C2 — Campos personalizados aplicables a una tarea/proyecto,
// agrupados en PESTAÑAS configurables. Una pestaña aplica según su scope, su
// equipo y (para tareas) el tipo de tarea. Los campos sin pestaña quedan
// "sueltos" y se muestran en la sección por defecto (comportamiento previo).

// Hook compartido: carga campos + pestañas y resuelve qué aplica a este contexto.
export function useCustomSchema(scope, departmentId, taskType) {
  const [fields, setFields] = useState([]);
  const [tabs, setTabs] = useState([]);

  useEffect(() => {
    const params = { scope };
    if (departmentId) params.department_id = departmentId;
    Promise.all([
      api.get("/custom-fields", { params }).then((r) => r.data || []).catch(() => []),
      api.get("/custom-tabs", { params }).then((r) => r.data || []).catch(() => []),
    ]).then(([f, t]) => { setFields(f); setTabs(t); });
  }, [scope, departmentId]);

  return useMemo(() => {
    // Una pestaña aplica si no tiene tipos (=> todas) o incluye el tipo actual.
    // En proyectos (taskType undefined) aplican todas.
    const applies = (t) => !taskType || !(t.task_types || []).length || t.task_types.includes(taskType);
    const applicableTabs = tabs.filter(applies);
    const applicableIds = new Set(applicableTabs.map((t) => t.id));
    const looseFields = fields.filter((f) => !f.tab_id);
    // Campos bajo una pestaña que NO aplica a este contexto → se ocultan.
    const tabFields = (id) => fields.filter((f) => f.tab_id === id);
    return { fields, tabs, applicableTabs, applicableIds, looseFields, tabFields };
  }, [fields, tabs, taskType]);
}

function FieldInput({ f, value, set, users }) {
  const v = value?.[f.key] ?? "";
  switch (f.type) {
    case "memo":
      return <textarea className="sel w-full" rows={3} value={v || ""} onChange={(e) => set(f.key, e.target.value)} />;
    case "number":
      return <input type="number" className="sel w-full" value={v}
        onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))} />;
    case "date":
      return <input type="date" className="sel w-full" value={v || ""} onChange={(e) => set(f.key, e.target.value)} />;
    case "select":
      return (
        <select className="sel w-full" value={v || ""} onChange={(e) => set(f.key, e.target.value)}>
          <option value="">—</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "person":
      return (
        <select className="sel w-full" value={v || ""} onChange={(e) => set(f.key, e.target.value ? Number(e.target.value) : "")}>
          <option value="">—</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
      );
    default:
      return <input className="sel w-full" value={v || ""} onChange={(e) => set(f.key, e.target.value)} />;
  }
}

// Formulario (crear/editar): campos sueltos + una sección titulada por pestaña.
export default function CustomFieldsForm({ scope, departmentId, taskType, value = {}, onChange, users = [] }) {
  const { looseFields, applicableTabs, tabFields } = useCustomSchema(scope, departmentId, taskType);
  const set = (key, v) => onChange({ ...(value || {}), [key]: v });

  const renderField = (f) => (
    <div key={f.id}>
      <label className="lbl">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
      <FieldInput f={f} value={value} set={set} users={users} />
    </div>
  );

  const tabSections = applicableTabs
    .map((t) => ({ t, ff: tabFields(t.id) }))
    .filter(({ ff }) => ff.length > 0);

  if (looseFields.length === 0 && tabSections.length === 0) return null;

  return (
    <div className="space-y-4">
      {looseFields.length > 0 && <div className="space-y-3">{looseFields.map(renderField)}</div>}
      {tabSections.map(({ t, ff }) => (
        <div key={t.id} className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t.name}</div>
          {ff.map(renderField)}
        </div>
      ))}
    </div>
  );
}

// Lista de valores (solo lectura) de un conjunto de campos. Con showEmpty
// muestra TODOS los campos (los vacíos como "—") — útil en la pestaña propia.
function ValueList({ fields, value, users, showEmpty = false }) {
  const rows = fields
    .map((f) => {
      let v = value?.[f.key];
      const empty = v === undefined || v === "" || v === null;
      if (empty && !showEmpty) return null;
      if (!empty && f.type === "person") v = users.find((u) => u.id === Number(v))?.full_name || v;
      return { label: f.label, value: empty ? "—" : String(v), memo: f.type === "memo" };
    })
    .filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div className="grid gap-x-6 sm:grid-cols-2">
      {rows.map((r, i) => (
        <div key={i} className={`border-b border-slate-50 py-1.5 text-sm last:border-0 ${r.memo ? "sm:col-span-2" : "flex items-start justify-between gap-3"}`}>
          <span className="shrink-0 text-slate-400">{r.label}</span>
          <span className={`font-medium text-slate-700 ${r.memo ? "mt-0.5 block whitespace-pre-wrap" : "text-right"}`}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// Vista (solo lectura). Sin tabId → campos sueltos (para la pestaña "Info").
// Con tabId → los campos de esa pestaña (para su pestaña propia en la ficha).
export function CustomFieldsView({ scope, departmentId, taskType, value = {}, users = [], tabId = null }) {
  const { looseFields, tabFields } = useCustomSchema(scope, departmentId, taskType);
  const fields = tabId ? tabFields(tabId) : looseFields;
  return <ValueList fields={fields} value={value} users={users} showEmpty={Boolean(tabId)} />;
}

// Todas las pestañas aplicables APILADAS como tarjetas ordenadas (solo lectura).
// Se usa en la ficha del proyecto para ver de un vistazo todas sus pestañas.
export function CustomTabsView({ scope, departmentId, taskType, value = {}, users = [] }) {
  const { applicableTabs, tabFields } = useCustomSchema(scope, departmentId, taskType);
  const sections = applicableTabs
    .map((t) => ({ t, ff: tabFields(t.id) }))
    .filter(({ ff }) => ff.length > 0);
  if (!sections.length) return null;
  return (
    <>
      {sections.map(({ t, ff }) => (
        <div key={t.id} className="card p-4">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
            <Icon name="fileText" size={13} /> {t.name}
          </div>
          <ValueList fields={ff} value={value} users={users} showEmpty />
        </div>
      ))}
    </>
  );
}
